import React, { useEffect, useState } from 'react';
import { t } from '../../i18n';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { realtyApi, isRealtyLoggedIn } from './realtyApi';
import RealtyPropertyCard from './RealtyPropertyCard';

function ComparisonModal({ propertyIds, onClose }) {
  const [properties, setProperties] = useState(null);

  useEffect(() => {
    realtyApi.compareProperties(propertyIds).then((data) => setProperties(data.properties)).catch(() => setProperties([]));
  }, [propertyIds]);

  const rows = [
    ['Price', (p) => `Rs ${Number(p.price).toLocaleString()}${p.purpose === 'rent' ? '/mo' : ''}`],
    ['Type', (p) => p.property_type],
    ['City / Area', (p) => `${p.city}, ${p.area}`],
    ['Size', (p) => `${p.area_size} ${p.area_unit}`],
    ['Bedrooms', (p) => p.bedrooms ?? '—'],
    ['Bathrooms', (p) => p.bathrooms ?? '—'],
    ['Agent', (p) => p.agent?.user?.username || '—'],
  ];

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '20px 12px', overflowY: 'auto' }} onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: 16, maxWidth: 900, width: '100%', padding: '1.5rem', marginTop: 20 }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ margin: 0, fontSize: 20 }}>{t('compare_properties')}</h2>
          <button onClick={onClose} style={{ border: 'none', background: 'transparent', fontSize: 20, cursor: 'pointer', color: '#5F5E5A' }}>✕</button>
        </div>
        {!properties ? <p className="muted">{t('loading')}</p> : properties.length === 0 ? <p className="muted">{t('could_not_load')}</p> : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: 8, borderBottom: '2px solid var(--border)' }}></th>
                  {properties.map((p) => (
                    <th key={p.id} style={{ textAlign: 'left', padding: 8, borderBottom: '2px solid var(--border)', minWidth: 160 }}>{p.title}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map(([label, fn]) => (
                  <tr key={label}>
                    <td style={{ padding: 8, fontWeight: 600, borderBottom: '1px solid var(--border)' }}>{label}</td>
                    {properties.map((p) => (
                      <td key={p.id} style={{ padding: 8, borderBottom: '1px solid var(--border)' }}>{fn(p)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function RealtyHelpModal({ onClose }) {
  const sectionStyle = { marginBottom: 20 };
  const headingStyle = { fontSize: 16, fontWeight: 600, margin: '0 0 8px' };
  const textStyle = { fontSize: 14, color: '#3A3935', lineHeight: 1.6, margin: '0 0 8px' };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '20px 12px', overflowY: 'auto' }} onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: 16, maxWidth: 640, width: '100%', padding: '1.5rem', marginTop: 20 }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ margin: 0, fontSize: 20 }}>How XpertRealty works</h2>
          <button onClick={onClose} style={{ border: 'none', background: 'transparent', fontSize: 20, cursor: 'pointer', color: '#5F5E5A' }}>✕</button>
        </div>

        <div style={sectionStyle}>
          <p style={headingStyle}>What is this?</p>
          <p style={textStyle}>A property listing platform for Pakistan - buy, sell, or rent homes, plots, and storefronts. Listing is free; the platform earns a 5% commission only when a deal actually closes.</p>
        </div>

        <div style={sectionStyle}>
          <p style={headingStyle}>If you're looking for a property</p>
          <p style={textStyle}>1. Search by Sale/Rent, category, and city on the home page. Save any search you like ("💾 Save this search") to reload it with one tap later.</p>
          <p style={textStyle}>2. Open a listing to see full details, photos, and specs. On "For Sale" listings, use the built-in EMI/mortgage calculator to estimate monthly payments.</p>
          <p style={textStyle}>3. Tick "Compare" on up to 4 listings to see them side-by-side (price, size, bedrooms, agent, etc.).</p>
          <p style={textStyle}>4. Send an inquiry directly to the owner/agent with your name and phone - they'll reach out to you. You can also rate any agent after dealing with them.</p>
        </div>

        <div style={sectionStyle}>
          <p style={headingStyle}>If you're listing a property</p>
          <p style={textStyle}>1. Connect with Pi Network to log in (automatic the first time you open this page in Pi Browser).</p>
          <p style={textStyle}>2. Post your property for free from "Post an Ad" - photos, price, size, location.</p>
          <p style={textStyle}>3. When you find a buyer/renter off-platform, mark the deal as closed in your Dashboard - this records the final price and calculates the 5% commission.</p>
          <p style={textStyle}>4. Pay that commission in Pi directly from your Dashboard once you know the Pi equivalent.</p>
          <p style={textStyle}>5. Boost a listing anytime for extra visibility. Good ratings from past clients help build trust with new buyers/renters.</p>
        </div>

        <div style={sectionStyle}>
          <p style={headingStyle}>Fees</p>
          <p style={textStyle}>Listing is always free. A 5% commission on the final agreed price applies only once you mark a deal as closed - nothing is owed before that.</p>
        </div>

        <div style={sectionStyle}>
          <p style={headingStyle}>FAQ</p>
          <p style={{ ...textStyle, fontWeight: 600, marginBottom: 2 }}>How do I know how much Pi to pay for commission?</p>
          <p style={textStyle}>The commission amount is shown in PKR - check the current Pi exchange rate and enter the equivalent Pi amount when paying.</p>
          <p style={{ ...textStyle, fontWeight: 600, marginBottom: 2 }}>Is there a dispute process if a deal falls through?</p>
          <p style={textStyle}>Not yet automated - contact platform support directly if something goes wrong after marking a deal closed.</p>
        </div>

        <button onClick={onClose} style={{ width: '100%', padding: '10px', fontSize: 14, fontWeight: 500, border: 'none', borderRadius: 8, background: '#0F6E56', color: '#fff', cursor: 'pointer' }}>
          Got it
        </button>
      </div>
    </div>
  );
}

export default function RealtyHome() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showHelp, setShowHelp] = useState(false);

  const [purpose, setPurpose] = useState(params.get('purpose') || '');
  const [category, setCategory] = useState(params.get('category') || '');
  const [city, setCity] = useState(params.get('city') || '');

  const [savedSearches, setSavedSearches] = useState([]);
  const [showSaved, setShowSaved] = useState(false);
  const [compareIds, setCompareIds] = useState([]);
  const [showCompare, setShowCompare] = useState(false);

  useEffect(() => {
    if (isRealtyLoggedIn()) {
      realtyApi.savedSearches().then((data) => setSavedSearches(data.results || data)).catch(() => {});
    }
  }, []);

  const toggleCompare = (id) => {
    setCompareIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : prev.length < 4 ? [...prev, id] : prev);
  };

  const saveCurrentSearch = async () => {
    const name = prompt('Name this search (e.g. "3BR Lahore under 2 Crore"):');
    if (!name) return;
    try {
      await realtyApi.saveSearch({ name, purpose, category, city });
      const data = await realtyApi.savedSearches();
      setSavedSearches(data.results || data);
      alert('Search saved!');
    } catch (e) {
      alert('Please connect with Pi Network first to save searches.');
    }
  };

  const applySavedSearch = (s) => {
    setPurpose(s.purpose || '');
    setCategory(s.category || '');
    setCity(s.city || '');
    const next = {};
    if (s.purpose) next.purpose = s.purpose;
    if (s.category) next.category = s.category;
    if (s.city) next.city = s.city;
    setParams(next);
    setShowSaved(false);
  };

  const deleteSavedSearch = async (id) => {
    await realtyApi.deleteSavedSearch(id);
    setSavedSearches((prev) => prev.filter((s) => s.id !== id));
  };

  useEffect(() => {
    setLoading(true);
    const query = Object.fromEntries(params.entries());
    realtyApi.listProperties(query)
      .then((data) => setProperties(data.results || []))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [params]);

  function handleSearch(e) {
    e.preventDefault();
    const next = {};
    if (purpose) next.purpose = purpose;
    if (category) next.category = category;
    if (city) next.city = city;
    setParams(next);
  }

  return (
    <div className="realty-scope">
      {showHelp && <RealtyHelpModal onClose={() => setShowHelp(false)} />}
      <div className="hero">
        <div className="container">
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
            <button onClick={() => setShowHelp(true)} style={{ padding: '5px 12px', fontSize: 12, fontWeight: 500, border: '1px solid rgba(255,255,255,0.4)', borderRadius: 20, background: 'rgba(255,255,255,0.1)', color: '#fff', cursor: 'pointer' }}>
              ❓ How it works
            </button>
          </div>
          <h1 className="display">Find your next home, plot, or storefront — anywhere in Pakistan.</h1>
          <p>List for free. Pay 5% only when a deal actually closes.</p>

          <form className="search-bar" onSubmit={handleSearch}>
            <select value={purpose} onChange={(e) => setPurpose(e.target.value)}>
              <option value="">{t('sale_or_rent')}</option>
              <option value="sale">{t('for_sale')}</option>
              <option value="rent">{t('for_rent')}</option>
            </select>
            <select value={category} onChange={(e) => setCategory(e.target.value)}>
              <option value="">{t('category_label')}</option>
              <option value="residential">{t('residential')}</option>
              <option value="commercial">{t('commercial')}</option>
            </select>
            <input
              type="text"
              placeholder={t('city_placeholder')}
              value={city}
              onChange={(e) => setCity(e.target.value)}
            />
            <button type="submit">{t('search_btn')}</button>
          </form>

          <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
            <button type="button" onClick={saveCurrentSearch} style={{ fontSize: 12, padding: '6px 12px', borderRadius: 16, border: '1px solid rgba(255,255,255,0.4)', background: 'rgba(255,255,255,0.1)', color: '#fff', cursor: 'pointer' }}>
              💾 Save this search
            </button>
            {savedSearches.length > 0 && (
              <button type="button" onClick={() => setShowSaved(!showSaved)} style={{ fontSize: 12, padding: '6px 12px', borderRadius: 16, border: '1px solid rgba(255,255,255,0.4)', background: 'rgba(255,255,255,0.1)', color: '#fff', cursor: 'pointer' }}>
                📋 My Saved Searches ({savedSearches.length})
              </button>
            )}
          </div>

          {showSaved && (
            <div style={{ background: '#fff', borderRadius: 10, padding: 10, marginTop: 10 }}>
              {savedSearches.map((s) => (
                <div key={s.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
                  <span onClick={() => applySavedSearch(s)} style={{ fontSize: 13, cursor: 'pointer', color: 'var(--ink)' }}>{s.name}</span>
                  <button onClick={() => deleteSavedSearch(s.id)} style={{ fontSize: 11, color: 'var(--danger)', background: 'none', border: 'none', cursor: 'pointer' }}>{t('remove_btn')}</button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="section container">
        <div className="section-head">
          <h2>{properties.length ? 'Latest listings' : 'Listings'}</h2>
          <span className="muted">{properties.length} result{properties.length !== 1 ? 's' : ''}</span>
        </div>

        {loading && <p className="muted">{t('loading_listings')}</p>}
        {error && <div className="error-box">{error}</div>}

        {!loading && !error && properties.length === 0 && (
          <div className="empty-state">
            <h3>{t('no_listings_match')}</h3>
            <p className="muted">{t('try_clearing_filters')}</p>
            <button className="btn btn-primary" style={{ marginTop: 14 }} onClick={() => navigate('/realty/post-ad')}>
              Post your property
            </button>
          </div>
        )}

        <div className="grid">
          {properties.map((p) => (
            <RealtyPropertyCard key={p.id} property={p} onToggleCompare={toggleCompare} isCompareChecked={compareIds.includes(p.id)} />
          ))}
        </div>
      </div>

      {compareIds.length >= 2 && (
        <div style={{ position: 'fixed', bottom: 20, left: '50%', transform: 'translateX(-50%)', background: 'var(--emerald-dark)', color: '#fff', padding: '10px 20px', borderRadius: 30, boxShadow: '0 8px 24px rgba(0,0,0,0.25)', display: 'flex', alignItems: 'center', gap: 12, zIndex: 100 }}>
          <span style={{ fontSize: 13 }}>{compareIds.length} selected</span>
          <button onClick={() => setShowCompare(true)} style={{ fontSize: 13, fontWeight: 600, padding: '6px 16px', borderRadius: 20, border: 'none', background: '#fff', color: 'var(--emerald-dark)', cursor: 'pointer' }}>{t('compare_btn')}</button>
          <button onClick={() => setCompareIds([])} style={{ fontSize: 12, background: 'none', border: 'none', color: '#fff', cursor: 'pointer' }}>{t('clear_btn')}</button>
        </div>
      )}

      {showCompare && <ComparisonModal propertyIds={compareIds} onClose={() => setShowCompare(false)} />}
    </div>
  );
}
